/*
 * 30-minute Rush promotion (storefront side).
 *
 * The backend (settings.rush_api_url) is the source of truth: it creates a
 * single-use, customer-locked discount code that Shopify itself expires at
 * `endsAt`. This script only shows the countdown, applies the code to the cart
 * and lets the shopper claim their one-time bonus.
 */
(() => {
  const cfgEl = document.getElementById('RushConfig');
  if (!cfgEl) return;
  let cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }

  const API = String(cfg.api || '').trim().replace(/\/+$/, '');
  const KEY = `rush:v1:${cfg.shop}`;
  const MIN = 60 * 1000;
  const COLLECTION_TEMPLATES = ['collection', 'list-collections'];

  // ---- Persistence (per browser) ---------------------------------------------------
  const saved = (() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || {};
    } catch (e) {
      return {};
    }
  })();
  let session = saved.session || null;
  let offset = saved.offset || 0; // server clock − local clock
  let bonusPrompted = saved.bonusPrompted || null; // session id we already showed the bonus popup for
  let serverCfg = null;
  let busy = false;

  function persist() {
    try {
      localStorage.setItem(KEY, JSON.stringify({ session, offset, bonusPrompted }));
    } catch (e) {}
  }

  function setSession(s) {
    if (!s) return;
    if (s.serverNow) offset = s.serverNow - Date.now();
    session = s;
    persist();
    render();
  }

  const now = () => Date.now() + offset;
  const t = (iso) => Date.parse(iso);

  // ---- API ------------------------------------------------------------------------
  async function api(path, body) {
    const res = await fetch(
      API + path,
      body
        ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        : undefined
    );
    let data = {};
    try {
      data = await res.json();
    } catch (e) {}
    if (!res.ok) {
      const err = new Error(data.message || 'Something went wrong. Please try again.');
      err.code = data.error || `http_${res.status}`;
      throw err;
    }
    return data;
  }

  // ---- Cart discount --------------------------------------------------------------
  async function applyDiscount(code) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
    } catch (e) {}
    try {
      // Also sets the checkout discount cookie, in case the cart is recreated.
      await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, { credentials: 'same-origin' });
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('rush:discount-applied', { detail: { code } }));
  }

  async function ensureDiscountOnCart() {
    if (phase() !== 'running') return;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const codes = (cart.discount_codes || []).map((d) => String(d.code).toUpperCase());
      if (!codes.includes(session.code.toUpperCase())) await applyDiscount(session.code);
    } catch (e) {}
  }

  // ---- Formatting -----------------------------------------------------------------
  const decoder = document.createElement('textarea');
  function decode(html) {
    decoder.innerHTML = html;
    return decoder.value;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  function formatMoney(cents) {
    const fmt = decode(String(cfg.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, ''));
    const kind = (fmt.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    const fix = (dp, thousands, decimal) => {
      const [i, f] = (cents / 100).toFixed(dp).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (f ? decimal + f : '');
    };
    const formats = {
      amount: () => fix(2, ',', '.'),
      amount_no_decimals: () => fix(0, ',', '.'),
      amount_with_comma_separator: () => fix(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fix(0, '.', ','),
      amount_with_apostrophe_separator: () => fix(2, "'", '.'),
      amount_with_space_separator: () => fix(2, ' ', ','),
    };
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, (formats[kind] || formats.amount)());
  }

  function formatClock(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
  }

  // ---- State ----------------------------------------------------------------------
  function bonusAvailable() {
    if (!session || session.bonusUsed || session.purchased) return false;
    const n = now();
    return n >= t(session.bonusFrom) && n <= t(session.bonusUntil);
  }

  // No per-person limit: once a Rush has ended (or its code was used at checkout) the shopper
  // is back to 'idle' and can start another one.
  function phase() {
    if (session && !session.purchased && now() < t(session.endsAt)) return 'running';
    if (bonusAvailable()) return 'bonus';
    return serverCfg && serverCfg.campaign === 'active' ? 'idle' : 'off';
  }

  function percent() {
    return (session && session.percent) || (serverCfg && serverCfg.percent) || 30;
  }

  // ---- Widgets --------------------------------------------------------------------
  const isCollection = COLLECTION_TEMPLATES.includes(cfg.template);
  let bar = null;
  let pill = null;

  function getBar() {
    if (!isCollection) return null;
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'rush-w rush-bar';
      const main = document.getElementById('MainContent') || document.querySelector('main');
      if (!main) return null;
      main.prepend(bar);
      positionBar();
    }
    return bar;
  }

  function getPill() {
    if (!pill) {
      pill = document.createElement('div');
      pill.className = 'rush-w rush-pill';
      document.body.appendChild(pill);
    }
    return pill;
  }

  // Keep the sticky bar just under the (possibly sticky / auto-hiding) header.
  let barTicking = false;
  function positionBar() {
    if (!bar || barTicking) return;
    barTicking = true;
    requestAnimationFrame(() => {
      barTicking = false;
      const header = document.querySelector('.section-header');
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      bar.style.top = `${Math.max(0, Math.round(bottom))}px`;
    });
  }
  window.addEventListener('scroll', positionBar, { passive: true });
  window.addEventListener('resize', positionBar);

  const bonusBtn = (label) =>
    `<button type="button" class="rush-btn rush-btn--bonus" data-rush-bonus>${esc(label)}</button>`;
  const startBtn = (label) => `<button type="button" class="rush-btn" data-rush-start>${esc(label)}</button>`;
  const closeBtn = `<button type="button" class="rush-x" data-rush-dismiss aria-label="Dismiss">&times;</button>`;

  function timerHtml(ms) {
    return `<span class="rush-clock" role="timer">${formatClock(ms)}</span>`;
  }

  // ---- Flip clock -----------------------------------------------------------------
  // Placeholder markup; setFlip() builds the digits and then only flips the ones that change,
  // so the widget's innerHTML stays stable while the clock ticks.
  const flipHtml = (size, labels) =>
    `<span class="rush-flip rush-flip--${size}" data-rush-flip role="timer"${labels ? ' data-labels' : ''}></span>`;

  const reduceMotion = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const pad2 = (n) => String(n).padStart(2, '0');
  const flipHalf = (cls, v) => `<span class="rf-h ${cls}"><span>${v}</span></span>`;

  function flipDigit(d, from, to) {
    clearTimeout(d._t);
    d.querySelectorAll('.rf-flap').forEach((f) => f.remove());
    const [top, bottom] = d.children;
    top.firstChild.textContent = to;
    if (reduceMotion) {
      bottom.firstChild.textContent = to;
      return;
    }
    // Top flap (old digit) folds down, then bottom flap (new digit) lands over the old bottom half.
    bottom.firstChild.textContent = from;
    d.insertAdjacentHTML('beforeend', flipHalf('rf-t rf-flap rf-flap--t', from) + flipHalf('rf-b rf-flap rf-flap--b', to));
    d._t = setTimeout(() => {
      bottom.firstChild.textContent = to;
      d.querySelectorAll('.rf-flap').forEach((f) => f.remove());
    }, 620);
  }

  function setFlip(el, ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const h = Math.floor(total / 3600);
    const parts = [
      [Math.floor((total % 3600) / 60), 'Minutes'],
      [total % 60, 'Seconds'],
    ];
    if (h) parts.unshift([h, 'Hours']);
    const value = parts.map(([v]) => pad2(v)).join('');

    if (el._layout !== parts.length) {
      const labels = el.hasAttribute('data-labels');
      el.innerHTML = parts
        .map(
          ([v, label], i) =>
            `${i ? '<span class="rf-sep" aria-hidden="true">:</span>' : ''}<span class="rf-g" aria-hidden="true"><span class="rf-ds">${pad2(v)
              .split('')
              .map((c) => `<span class="rf-d">${flipHalf('rf-t', c)}${flipHalf('rf-b', c)}</span>`)
              .join('')}</span>${labels ? `<span class="rf-l">${label}</span>` : ''}</span>`
        )
        .join('');
      el._layout = parts.length;
      el.classList.toggle('rush-flip--hours', !!h);
      el._digits = [...el.querySelectorAll('.rf-d')];
    } else if (el._value !== value) {
      el._digits.forEach((d, i) => {
        if (el._value[i] !== value[i]) flipDigit(d, el._value[i], value[i]);
      });
    }
    el._value = value;
    el.setAttribute('aria-label', `${formatClock(ms)} remaining`);
  }

  function contentFor(kind, p, el) {
    const pct = percent();
    const remaining = session ? t(session.endsAt) - now() : 0;
    const bonusLabel = `+${(session && session.bonusMinutes) || (serverCfg && serverCfg.bonusMinutes) || 30} min bonus`;

    switch (p) {
      case 'idle':
        if (kind === 'bar')
          return `<span class="rush-text">${esc(cfg.text.barIdle)}</span>${startBtn('Start now')}`;
        if (kind === 'slot') {
          const price = Number(el.dataset.price);
          const eligible = el.dataset.eligible === 'true';
          return eligible && price
            ? `<span class="rush-text">Get it for <strong>${esc(formatMoney(price - Math.round((price * pct) / 100)))}</strong> in a ${esc(
                (serverCfg && serverCfg.durationMinutes) || 30
              )}-minute Rush</span>${startBtn('Start my Rush')}`
            : `<span class="rush-text">${esc(cfg.text.barIdle)}</span>${startBtn('Start my Rush')}`;
        }
        return sessionStorage.getItem(`${KEY}:pill-hidden`)
          ? ''
          : `<button type="button" class="rush-pill__cta" data-rush-start>⏱ ${esc(cfg.text.cta)}</button>${closeBtn}`;

      case 'running': {
        const bonus = bonusAvailable() ? bonusBtn(bonusLabel) : '';
        const mute = kind === 'card' ? '' : muteBtn();
        if (kind === 'slot') {
          const price = Number(el.dataset.price);
          const eligible = el.dataset.eligible === 'true';
          const priceLine =
            eligible && price
              ? `<span class="rush-price">Your Rush price <strong>${esc(
                  formatMoney(price - Math.round((price * pct) / 100))
                )}</strong> <s>${esc(formatMoney(price))}</s> <span class="rush-badge">−${pct}%</span></span>`
              : `<span class="rush-price rush-price--na">Sale items aren't included in the Rush</span>`;
          return `${mute}<span class="rush-eyebrow">${pct}% off ends in</span>${flipHtml('lg', true)}${bonus}${priceLine}`;
        }
        if (kind === 'bar')
          return `${mute}<span class="rush-eyebrow">Your Rush is on · ${pct}% off full-price items</span>${flipHtml(
            'xl',
            true
          )}${bonus}`;
        if (kind === 'card') return `<span class="rush-card__cap">${pct}% off · ends in</span>${flipHtml('md')}`;
        return `<span class="rush-text">${timerHtml(remaining)} <small>${pct}% off</small></span>${bonus}${mute}`;
      }

      case 'bonus': {
        const until = t(session.bonusUntil) - now();
        const text = `Time's up! Claim your bonus in ${formatClock(until)}`;
        return `<span class="rush-text">${esc(text)}</span>${bonusBtn(bonusLabel)}`;
      }

      default:
        return '';
    }
  }

  function paint(el, kind, p, mods) {
    if (!el) return;
    // Idle/bonus text etc. only apply to the bar, slot and pill; cards show just the running clock.
    const html = kind === 'card' && p !== 'running' ? '' : contentFor(kind, p, el);
    if (el._html !== html) {
      el._html = html;
      el.innerHTML = html;
    }
    el.hidden = !html;
    el.classList.toggle('rush-w--warning', mods.warning);
    el.classList.toggle('rush-w--flash', mods.flash);
    el.classList.toggle('rush-w--idle', p === 'idle');
    el.classList.toggle('rush-w--running', p === 'running');
    const eligible = el.dataset.eligible === 'true';
    if (kind === 'slot') el.classList.toggle('rush-w--priced', p === 'running' && eligible && Number(el.dataset.price) > 0);
    if (kind === 'card') el.classList.toggle('rush-card--na', !eligible);
    if (p === 'running') el.querySelectorAll('[data-rush-flip]').forEach((f) => setFlip(f, mods.remaining));
  }

  // Collection card price: strike through the regular price and show the Rush price.
  function paintCardPrice(el, p) {
    const price = Number(el.dataset.price);
    const on = p === 'running' && el.dataset.eligible === 'true' && price > 0;
    const pct = percent();
    const html = on
      ? `${el.dataset.varies === 'true' ? '<span class="rush-cp__from">From</span>' : ''}<strong class="rush-cp__new">${esc(
          formatMoney(price - Math.round((price * pct) / 100))
        )}</strong><s class="rush-cp__old">${esc(formatMoney(price))}</s><span class="rush-badge">−${pct}%</span>`
      : '';
    if (el._html !== html) {
      el._html = html;
      el.innerHTML = html;
    }
    el.hidden = !on;
    const card = el.closest('.card-wrapper');
    if (card) card.classList.toggle('rush-priced', on);
  }

  let lastAnnounced = null;
  function announce(msg) {
    const live = document.getElementById('RushLive');
    if (live) live.textContent = msg;
  }

  function render() {
    const p = phase();
    const remaining = session ? t(session.endsAt) - now() : Infinity;
    const warning = (p === 'running' && remaining <= cfg.warningMinutes * MIN) || p === 'bonus';
    const flash = !!cfg.flash && p === 'running' && remaining <= cfg.flashSeconds * 1000;
    const mods = { warning, flash, remaining };

    // Excluded products (accessories, bags, shoes, perfume, sale…) get no Rush UI: nothing on their
    // product page, and no banner/card clocks on a collection page with no eligible product on it.
    const slots = document.querySelectorAll('[data-rush-slot]');
    slots.forEach((el) => paint(el, 'slot', el.dataset.eligible === 'true' ? p : 'off', mods));
    // Cards are re-queried every tick: filtering/sorting swaps the grid markup.
    const cards = document.querySelectorAll('[data-rush-card]');
    const quietCollection = cfg.template === 'collection' && ![...cards].some((el) => el.dataset.eligible === 'true');
    paint(getBar(), 'bar', quietCollection ? 'off' : p, mods);
    cards.forEach((el) => paint(el, 'card', quietCollection ? 'off' : p, mods));
    document.querySelectorAll('[data-rush-card-price]').forEach((el) => paintCardPrice(el, p));
    // "30% off" corner ribbons: advertise the offer before and during the shopper's Rush.
    const ribbons = p === 'idle' || p === 'running';
    document.querySelectorAll('[data-rush-ribbon]').forEach((el) => (el.hidden = !ribbons));
    const pct = String(percent());
    document.querySelectorAll('[data-rush-pct]').forEach((el) => {
      if (el.textContent !== pct) el.textContent = pct;
    });

    // Floating pill on every other page (not on collection/product pages, which have their own UI
    // or are deliberately quiet for excluded products).
    const hasInline = !!bar || slots.length > 0;
    paint(getPill(), 'pill', hasInline ? 'off' : p, mods);

    renderSound(p, remaining);
    maybeBonusPopup(p);

    document.documentElement.classList.toggle('rush-flashing', flash);
    document.documentElement.style.setProperty('--rush-flash-alpha', String((cfg.flashStrength || 30) / 100));

    // Screen-reader announcements at key moments (not every second).
    let mark = null;
    if (p === 'running' && remaining <= MIN) mark = '1';
    else if (p === 'running' && remaining <= cfg.warningMinutes * MIN) mark = 'warn';
    else if (p === 'bonus') mark = 'bonus';
    if (mark && mark !== lastAnnounced) {
      announce(
        mark === '1'
          ? 'Less than one minute left in your Rush.'
          : mark === 'warn'
          ? `${cfg.warningMinutes} minutes left in your Rush.`
          : "Your Rush time is up. You can still claim bonus time."
      );
    }
    lastAnnounced = mark;
  }

  // ---- Sounds ---------------------------------------------------------------------
  // Web Audio so the per-second flip tick is instant and can overlap the looping alarm.
  // Browsers keep the audio context suspended until the shopper clicks/taps/types on the page.
  const snd = cfg.sound || {};
  let muted = (() => {
    try {
      return localStorage.getItem(`${KEY}:muted`) === '1';
    } catch (e) {
      return false;
    }
  })();
  let actx = null;
  const buffers = {};
  let alarmNode = null;
  let lastTick = null;

  function audioCtx() {
    if (!actx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) actx = new AC();
    }
    return actx;
  }

  function loadSounds() {
    if (!snd.enabled || !snd.urls || !audioCtx()) return;
    Object.keys(snd.urls).forEach((name) => {
      if (name in buffers) return;
      buffers[name] = null;
      fetch(snd.urls[name])
        .then((r) => r.arrayBuffer())
        .then((data) => new Promise((ok, fail) => actx.decodeAudioData(data, ok, fail)))
        .then((buf) => (buffers[name] = buf))
        .catch(() => {});
    });
  }

  function playSound(name, { loop = false, volume = 1 } = {}) {
    if (!snd.enabled || muted || document.hidden) return null;
    const ctx = audioCtx();
    const buf = buffers[name];
    if (!ctx || !buf || ctx.state !== 'running') return null;
    const src = ctx.createBufferSource();
    const gain = ctx.createGain();
    src.buffer = buf;
    src.loop = loop;
    gain.gain.value = volume;
    src.connect(gain).connect(ctx.destination);
    src.start();
    return src;
  }

  function stopAlarm() {
    if (!alarmNode) return;
    try {
      alarmNode.stop();
    } catch (e) {}
    alarmNode = null;
  }

  function unlockAudio() {
    const ctx = snd.enabled && audioCtx();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  }
  ['pointerdown', 'keydown', 'touchend'].forEach((ev) => document.addEventListener(ev, unlockAudio, { capture: true, passive: true }));
  document.addEventListener('visibilitychange', () => document.hidden && stopAlarm());

  function muteBtn() {
    if (!snd.enabled) return '';
    return `<button type="button" class="rush-mute" data-rush-mute aria-pressed="${muted}" aria-label="${
      muted ? 'Turn countdown sound on' : 'Mute countdown sound'
    }">${muted ? '🔇' : '🔊'}</button>`;
  }

  function renderSound(p, remaining) {
    if (!snd.enabled) return;
    if (p === 'running' || p === 'bonus') loadSounds();
    const alarmOn = p === 'running' && remaining <= (snd.alarmSeconds || 30) * 1000;

    if (alarmOn && !muted && !document.hidden) {
      if (!alarmNode) alarmNode = playSound('alarm', { loop: true, volume: 0.55 });
    } else stopAlarm();

    // One flip tick per second (not per clock), only where a flip clock is on screen.
    const sec = p === 'running' ? Math.ceil(remaining / 1000) : null;
    if (sec !== null && sec !== lastTick && lastTick !== null) {
      const wantTick = snd.flip === 'always' || (snd.flip === 'final' && alarmOn);
      if (wantTick && document.querySelector('[data-rush-flip]')) playSound('flip', { volume: 0.7 });
    }
    lastTick = sec;
  }

  // ---- Start dialog ---------------------------------------------------------------
  const modal = document.getElementById('RushModal');
  const form = modal && modal.querySelector('.rush-form');
  const field = (n) => form.elements.namedItem(n);

  // After a successful start on a page with no Rush product grid (homepage, pages, blog...),
  // send the shopper to the collection so they spend the countdown shopping.
  const NO_REDIRECT_TEMPLATES = ['collection', 'list-collections', 'product'];
  let pendingRedirect = null;
  let redirectTimer = null;

  function redirectTarget() {
    const url = String(cfg.startRedirect || '').trim();
    if (!url || NO_REDIRECT_TEMPLATES.includes(cfg.template)) return null;
    return url;
  }

  function armRedirect() {
    pendingRedirect = redirectTarget();
    if (!pendingRedirect) return;
    const btn = modal.querySelector('[data-rush-panel="message"] [data-rush-close]');
    if (btn) btn.textContent = 'Start shopping';
    clearTimeout(redirectTimer);
    redirectTimer = setTimeout(goRedirect, 7000);
  }

  function goRedirect() {
    if (!pendingRedirect) return;
    const url = pendingRedirect;
    pendingRedirect = null;
    clearTimeout(redirectTimer);
    location.href = url;
  }

  function showPanel(name) {
    modal.querySelectorAll('[data-rush-panel]').forEach((el) => (el.hidden = el.dataset.rushPanel !== name));
  }

  function showMessage(title, text) {
    modal.querySelector('[data-rush-message-title]').textContent = title;
    modal.querySelector('[data-rush-message-text]').textContent = text;
    showPanel('message');
    if (!modal.open) modal.showModal();
  }

  // When the timer runs out, offer the one-time bonus in the dialog (once per Rush).
  function maybeBonusPopup(p) {
    if (!cfg.bonusPopup || !modal || p !== 'bonus' || !session || bonusPrompted === session.id) return;
    if (modal.open) return; // don't interrupt a dialog the shopper is using
    bonusPrompted = session.id;
    persist();
    const mins = String(session.bonusMinutes || (serverCfg && serverCfg.bonusMinutes) || 30);
    modal.querySelectorAll('[data-rush-bonus-min]').forEach((el) => (el.textContent = mins));
    showPanel('bonus');
    modal.showModal();
    playSound('bonus');
  }

  function openModal(auto = false) {
    if (!modal) return;
    const p = phase();
    if (p !== 'idle') {
      if (p === 'off' && serverCfg) showMessage('Rush not available', 'This promotion is not running right now.');
      return; // running/bonus: already going
    }
    const c = cfg.customer;
    if (c) {
      if (c.name && !field('name').value) field('name').value = c.name;
      field('email').value = c.email;
      field('email').readOnly = true;
      if (c.phone && !field('phone').value) field('phone').value = c.phone;
    }
    setError('');
    showPanel('form');
    modal.showModal();
    if (auto) {
      // Popped up on its own: show the poster from the top instead of jumping to the first field.
      modal.querySelector('.rush-modal__close').focus({ preventScroll: true });
      modal.scrollTop = 0;
    } else {
      (form.querySelector('input:not([readonly]):placeholder-shown, input:not([readonly])') || form).focus();
    }
  }

  function setError(msg) {
    const el = form.querySelector('.rush-form__error');
    el.textContent = msg;
    el.hidden = !msg;
  }

  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (busy) return;
      const data = new FormData(form);
      const payload = {
        name: String(data.get('name') || '').trim(),
        email: String(data.get('email') || '').trim(),
        phone: String(data.get('phone') || '').trim(),
        marketing: data.get('marketing') === '1',
        customerToken: (cfg.customer && cfg.customer.token) || undefined,
      };
      if (!payload.name) return setError('Please enter your name.');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(payload.email)) return setError('Please enter a valid email address.');
      if (payload.phone.replace(/\D/g, '').length < 9) return setError('Please enter a valid mobile number.');

      busy = true;
      setError('');
      const submit = form.querySelector('[type="submit"]');
      submit.setAttribute('aria-busy', 'true');
      submit.disabled = true;
      try {
        const res = await api('/api/rush/start', payload);
        setSession(res.session);
        await applyDiscount(res.session.code);
        showMessage(
          res.resumed ? 'Welcome back – your Rush is still on!' : 'Your Rush has started!',
          `You have ${formatClock(t(res.session.endsAt) - now())} to shop. ${res.session.percent}% off full-price items is applied automatically at checkout (code ${res.session.code}) – just check out with ${payload.email}.`
        );
        armRedirect();
      } catch (err) {
        setError(err.message);
      } finally {
        busy = false;
        submit.removeAttribute('aria-busy');
        submit.disabled = false;
      }
    });

    modal.addEventListener('close', goRedirect);

    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.closest('[data-rush-close]')) modal.close();
    });
  }

  // ---- Bonus ----------------------------------------------------------------------
  async function claimBonus(btn) {
    if (busy || !session) return;
    busy = true;
    btn.disabled = true;
    try {
      const res = await api(`/api/rush/session/${encodeURIComponent(session.id)}/bonus`, {});
      setSession(res.session);
      await applyDiscount(res.session.code);
      announce(`Bonus claimed. ${formatClock(t(res.session.endsAt) - now())} left.`);
      if (modal) showMessage('Bonus time added!', `You now have ${formatClock(t(res.session.endsAt) - now())} left in your Rush.`);
    } catch (err) {
      if (err.code === 'already_purchased' || err.code === 'bonus_used' || err.code === 'expired') sync();
      if (modal) showMessage("Couldn't add bonus time", err.message);
    } finally {
      busy = false;
      btn.disabled = false;
    }
  }

  // ---- Events ---------------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const start = e.target.closest('[data-rush-start]');
    if (start) {
      e.preventDefault();
      openModal();
      return;
    }
    const bonus = e.target.closest('[data-rush-bonus]');
    if (bonus) {
      e.preventDefault();
      claimBonus(bonus);
      return;
    }
    if (e.target.closest('[data-rush-mute]')) {
      muted = !muted;
      try {
        localStorage.setItem(`${KEY}:muted`, muted ? '1' : '0');
      } catch (err) {}
      if (muted) stopAlarm();
      render();
      return;
    }
    if (e.target.closest('[data-rush-dismiss]')) {
      try {
        sessionStorage.setItem(`${KEY}:pill-hidden`, '1');
      } catch (err) {}
      render();
    }
  });

  // "Buy it now" (dynamic checkout) goes straight to checkout and skips the cart, so the code
  // we put on the cart never reaches it. While a Rush is running, send the shopper to checkout
  // ourselves with the code attached: a cart permalink (just this item, like Buy it now), or
  // add-to-cart + /checkout when the form carries line-item properties / a selling plan.
  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target.closest('.shopify-payment-button__button, shopify-buy-it-now-button, [data-shopify="payment-button"]');
      if (!btn || phase() !== 'running') return;
      const form = btn.closest('form') || btn.closest('product-info, .product')?.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      const data = new FormData(form);
      const id = data.get('id');
      if (!id) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const qty = Math.max(1, parseInt(data.get('quantity'), 10) || 1);
      const code = encodeURIComponent(session.code);
      const extra = [...data.keys()].some((k) => k.startsWith('properties[') || k === 'selling_plan');
      if (!extra) {
        location.href = `/cart/${encodeURIComponent(id)}:${qty}?discount=${code}`;
        return;
      }
      try {
        await fetch('/cart/add.js', { method: 'POST', body: data });
      } catch (err) {}
      location.href = `/checkout?discount=${code}`;
    },
    true
  );

  // Keep product slot prices in sync with the selected variant (Dawn pub/sub).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event && event.data && event.data.variant;
      if (!variant) return;
      document.querySelectorAll('[data-rush-slot]').forEach((el) => {
        el.dataset.price = variant.price;
        el._html = null;
      });
      render();
    });
  }

  async function sync() {
    if (!session) return;
    try {
      const res = await api(`/api/rush/session/${encodeURIComponent(session.id)}`);
      setSession(res.session);
    } catch (err) {
      if (err.code === 'no_session') {
        session = null;
        persist();
        render();
      }
    }
  }

  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    try {
      const v = JSON.parse(e.newValue) || {};
      session = v.session || null;
      offset = v.offset || 0;
      bonusPrompted = v.bonusPrompted || null;
      render();
    } catch (err) {}
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sync();
  });

  // ---- Auto popups ----------------------------------------------------------------
  // True the first time it's called for `name` in this browser session.
  function once(name) {
    try {
      if (sessionStorage.getItem(`${KEY}:${name}`)) return false;
      sessionStorage.setItem(`${KEY}:${name}`, '1');
    } catch (e) {}
    return true;
  }

  // Other pages: pop up once per visit after the shopper has been inactive for a while.
  function watchIdle() {
    const idleMs = (Number(cfg.idleSeconds) || 0) * 1000;
    try {
      if (!idleMs || sessionStorage.getItem(`${KEY}:idle`)) return;
    } catch (e) {}
    let last = Date.now();
    const events = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    const bump = () => (last = Date.now());
    const stop = () => {
      clearInterval(timer);
      events.forEach((ev) => window.removeEventListener(ev, bump, true));
    };
    const timer = setInterval(() => {
      if (phase() !== 'idle') return stop(); // started from a button meanwhile, or promo off
      if (document.hidden || (modal && modal.open) || Date.now() - last < idleMs) return;
      stop();
      if (once('idle')) openModal(true);
    }, 1000);
    events.forEach((ev) => window.addEventListener(ev, bump, { capture: true, passive: true }));
  }

  // ---- Boot -----------------------------------------------------------------------
  async function boot() {
    render();
    const params = new URLSearchParams(location.search);
    const wantsStart = params.get('rush') === 'start';
    if (wantsStart) {
      params.delete('rush');
      const q = params.toString();
      history.replaceState(history.state, '', location.pathname + (q ? `?${q}` : '') + location.hash);
    }

    try {
      serverCfg = await api('/api/rush/config');
      if (!session && serverCfg.serverNow) offset = serverCfg.serverNow - Date.now();
    } catch (e) {
      serverCfg = null; // backend unreachable: keep the promo UI hidden
    }

    // Signed-in shopper on a new device: pick up their running session.
    if (!session && cfg.customer && cfg.customer.token && serverCfg) {
      try {
        const res = await api('/api/rush/resume', { customerToken: cfg.customer.token });
        setSession(res.session);
      } catch (e) {}
    } else if (session) {
      await sync();
    }

    render();
    ensureDiscountOnCart();

    if (phase() === 'idle') {
      // Pop up once per browser session: on the homepage, or on any page with "autoOpen".
      const autoHere = cfg.autoOpen || (cfg.autoOpenHome && cfg.template === 'index');
      if (wantsStart) openModal();
      else if (autoHere && once('auto')) openModal(true);
      else if (cfg.template !== 'index') watchIdle();
    }

    setInterval(render, 1000);
    setInterval(sync, MIN);
  }

  if (!API) return;
  boot();
})();
